import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../lib/store';
import { Icon } from '../components/Icon';
import { Cover } from '../components/Cover';
import { IconBtn, TopBar } from '../components/ui';
import { getDB, getBook, putChapters } from '../lib/db';
import { parseBook, type ParsedBook } from '../lib/parsers';
import { pushBookToMailbox } from '../lib/sync';

type Phase =
  | { kind: 'idle' }
  | { kind: 'parsing'; msg: string; pct: number }
  | { kind: 'done'; book: ParsedBook }
  | { kind: 'error'; message: string };

async function fileHash(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const h = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(h)).slice(0, 8).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const COVER_ACCENTS = ['#0F3D2C', '#D97757', '#8B1A1A', '#C4A86A', '#7A6AA8', '#1F5A42'];

export function Upload() {
  const nav = useNavigate();
  const addBook = useStore((s) => s.addBook);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [fileName, setFileName] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);
  const recent = useStore((s) => s.books).slice(0, 3);

  const onPick = async (f: File) => {
    setFileName(f.name);
    setPhase({ kind: 'parsing', msg: 'Starting...', pct: 0 });
    try {
      const parsed = await parseBook(f, (p) => {
        setPhase({ kind: 'parsing', msg: p.phase, pct: p.pct ?? 0 });
      });

      setPhase({ kind: 'parsing', msg: 'Saving...', pct: 95 });
      const id = await fileHash(f);
      const db = await getDB();
      await db.put('files', { id, blob: f });
      await putChapters(id, parsed.chapters);
      // Verify the write landed
      const verifyCount = (await db.getAllFromIndex('chapters', 'by-book', id)).length;
      console.log('[upload] id=%s, parsed chapters=%d, saved to idb=%d', id, parsed.chapters.length, verifyCount);
      if (verifyCount !== parsed.chapters.length) {
        console.warn('[upload] chapter save mismatch!', { parsed: parsed.chapters.length, saved: verifyCount });
      }

      const accent = COVER_ACCENTS[Math.floor(Math.random() * COVER_ACCENTS.length)];
      await addBook({
        id,
        title: parsed.title || f.name,
        author: parsed.author || 'Unknown',
        pages: parsed.pages,
        chapters: parsed.chapters.length,
        progress: 0,
        pinned: true,
        cover_kind: 'designer',
        cover_data: parsed.coverDataUrl || null,
        accent,
        file_hash: id,
        added_at: Date.now(),
        kind: parsed.kind,
        lang: parsed.lang,
      });
      // Fire-and-forget push to Supabase mailbox so the laptop worker can relay to SiYuan.
      // We don't await - upload completion shouldn't block on network.
      getBook(id).then((b) => b && pushBookToMailbox(b)).catch((e) => console.warn('[upload] mailbox push failed', e));
      setPhase({ kind: 'done', book: parsed });
    } catch (e: any) {
      console.error('[upload] parse failed', e);
      setPhase({ kind: 'error', message: e?.message || String(e) });
    }
  };

  return (
    <div className="flex flex-col h-full safe-top">
      <TopBar
        left={<IconBtn name="back" onClick={() => nav('/')} />}
        title={<span>{phase.kind === 'idle' ? 'Add a book' : phase.kind === 'done' ? 'Book added' : phase.kind === 'error' ? 'Error' : 'Adding book'}</span>}
      />

      {phase.kind === 'idle' && (
        <div className="flex-1 px-5 pt-4">
          <input ref={fileRef} type="file" accept=".pdf,.epub,.txt,.md,.markdown" hidden onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])} />
          <button onClick={() => fileRef.current?.click()} className="w-full rounded-3xl border-2 border-dashed border-ink-300 dark:border-white/15 py-12 flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-forest-500/10 text-forest-500 flex items-center justify-center">
              <Icon name="upload" className="w-6 h-6" strokeWidth={1.6} />
            </div>
            <div className="text-center">
              <div className="font-medium text-[15px]">Drop or select</div>
              <div className="text-[12px] text-ink-500 mt-0.5">PDF · EPUB · TXT · Markdown · Up to 120 MB</div>
            </div>
            <span className="mt-2 h-10 px-5 rounded-full bg-ink-900 dark:bg-cream-100 text-cream-50 dark:text-ink-900 text-[13px] font-medium flex items-center">Choose file</span>
          </button>

          {recent.length > 0 && (
            <div className="mt-5 space-y-2">
              <div className="text-[11px] uppercase tracking-[.2em] text-ink-500 px-1 mb-2">Recently added</div>
              {recent.map((b) => (
                <button key={b.id} onClick={() => nav(`/book/${b.id}`)} className="w-full text-left flex items-center gap-3 p-2.5 rounded-2xl bg-paper dark:bg-night-800 shadow-soft">
                  <Cover kind={b.cover_kind} dataUrl={b.cover_data} className="w-9 h-[54px] rounded-md" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{b.title}</div>
                    <div className="text-[11px] text-ink-500 truncate">{b.author} · {b.pages} pages</div>
                  </div>
                  <Icon name="check" className="w-4 h-4 text-forest-500" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {phase.kind === 'parsing' && (
        <div className="flex-1 px-5 pt-10 flex flex-col items-center">
          <div className="w-[140px] h-[200px] rounded-xl bg-cream-100 dark:bg-night-700 shadow-card flex items-center justify-center">
            <div className="waveform flex h-8 text-forest-500"><span /><span /><span /><span /><span /></div>
          </div>
          <div className="text-center mt-5">
            <div className="font-display text-[22px] leading-tight truncate max-w-[320px]">{fileName}</div>
            <div className="text-[12px] text-ink-500 mt-0.5">{phase.msg}</div>
          </div>
          <div className="w-full max-w-[280px] mt-5">
            <div className="h-[3px] rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
              <div className="h-full bg-forest-500 transition-all" style={{ width: `${phase.pct}%` }} />
            </div>
            <div className="text-[10px] text-ink-500 mt-1.5 text-center tabular-nums">{phase.pct}%</div>
          </div>
          <p className="mt-8 text-[11px] text-ink-500 text-center max-w-[280px]">Don't close this screen. Large EPUBs/PDFs may take 10-30 seconds.</p>
        </div>
      )}

      {phase.kind === 'done' && (
        <div className="flex-1 px-5 pt-8 overflow-y-auto no-scrollbar pb-8">
          <Cover
            kind="designer"
            dataUrl={phase.book.coverDataUrl}
            className="w-[140px] h-[200px] rounded-xl mx-auto"
          />
          <div className="text-center mt-5">
            <div className="font-display text-[22px] leading-tight">{phase.book.title}</div>
            <div className="text-[12px] text-ink-500 mt-0.5">{phase.book.author}</div>
            <div className="text-[11px] text-ink-400 mt-0.5">{phase.book.pages} pages · <b className={phase.book.chapters.length === 0 ? 'text-coral' : ''}>{phase.book.chapters.length} chapters</b> · {phase.book.kind.toUpperCase()}</div>
            {phase.book.chapters.length === 0 && (
              <div className="mt-3 text-[11px] text-coral">No chapters extracted - file may be encrypted, image-only, or malformed.</div>
            )}
          </div>

          {/* DEBUG panel - shows exact parsed state so user can screenshot if anything looks wrong */}
          <div className="mt-5 rounded-2xl bg-cream-100 dark:bg-night-700 p-3 text-[11px] font-mono text-ink-700 dark:text-ink-300 space-y-1">
            <div className="text-[10px] uppercase tracking-[.15em] text-ink-500 font-sans mb-1">Parse result</div>
            <div>cover: <b>{phase.book.coverDataUrl ? 'yes' : 'none'}</b></div>
            <div>chapters: <b className={phase.book.chapters.length === 0 ? 'text-coral' : ''}>{phase.book.chapters.length}</b></div>
            <div>total words: <b>{phase.book.chapters.reduce((a, c) => a + c.text.split(/\s+/).length, 0).toLocaleString()}</b></div>
            {phase.book.chapters[0] && (
              <div className="pt-2 border-t border-ink-300/30">
                <div className="font-sans text-[10px] uppercase tracking-[.15em] text-ink-500">Ch 1: {phase.book.chapters[0].title}</div>
                <div className="mt-1 line-clamp-4 font-serif text-[12px] leading-snug italic">"{phase.book.chapters[0].text.slice(0, 240)}..."</div>
              </div>
            )}
            {phase.book.chapters[1] && (
              <div className="pt-2 border-t border-ink-300/30">
                <div className="font-sans text-[10px] uppercase tracking-[.15em] text-ink-500">Ch 2: {phase.book.chapters[1].title}</div>
                <div className="mt-1 line-clamp-3 font-serif text-[12px] leading-snug italic">"{phase.book.chapters[1].text.slice(0, 180)}..."</div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2.5 mt-7">
            <button onClick={() => nav('/')} className="h-12 rounded-full bg-paper dark:bg-night-800 shadow-soft font-medium text-[14px]">Library</button>
            <button onClick={async () => { const id = await fileHash(await fetch('data:,').then(() => ({} as any)).catch(() => null) as any); /* noop */ }} className="hidden" />
            <a href={`#/read`} onClick={(e) => { e.preventDefault(); const all = useStore.getState().books; const latest = [...all].sort((a, b) => b.added_at - a.added_at)[0]; if (latest) nav(`/read/${latest.id}`); }} className="h-12 rounded-full bg-ink-900 dark:bg-cream-100 text-cream-50 dark:text-ink-900 font-medium text-[14px] flex items-center justify-center">
              Open book
            </a>
          </div>
        </div>
      )}

      {phase.kind === 'error' && (
        <div className="flex-1 px-5 pt-8">
          <div className="rounded-2xl bg-coral/10 border border-coral/30 p-5 text-center">
            <div className="w-12 h-12 rounded-full bg-coral/20 text-coral mx-auto flex items-center justify-center mb-3">
              <Icon name="close" className="w-5 h-5" />
            </div>
            <div className="font-medium text-[14px]">Couldn't parse file</div>
            <p className="text-[12px] text-ink-500 mt-1 break-words">{phase.message}</p>
          </div>
          <button onClick={() => setPhase({ kind: 'idle' })} className="w-full h-12 mt-4 rounded-full bg-ink-900 dark:bg-cream-100 text-cream-50 dark:text-ink-900 font-medium text-[14px]">Try another file</button>
        </div>
      )}
    </div>
  );
}
